/**
 * @file KeyboardController.js
 * @description Centralized keyboard handler that wires transport, dimension, and panel shortcuts.
 * Accounts for iframe focus, prevents repeated key spam, and keeps shortcuts layered for future expansion.
 *
 * In a shared session a deck is somebody's to drive, and this file is one of the two places that
 * has to know it. A pointer never did: a deck this player does not hold wears a full-cell cover, so
 * the mouse physically cannot reach what is under it. A keyboard has no cell to cover — these
 * listeners sit on the document and aim at whatever is focused — so every shortcut here reached
 * straight past the room's gates and moved a deck the player had no claim on. The audible half was
 * caught further down (the engine and the parameter manager refuse the write), which made it worse
 * rather than better: the key did nothing, the interface moved anyway, and the two disagreed.
 *
 * So the gate lives HERE, once, at the entry point — not in each handler. Every shortcut DECLARES
 * what it acts on (`scope`); `handleKeyDown` resolves that to the decks this player may drive and
 * refuses the key when there are none. Handlers are handed their targets and never learn a room
 * exists. A new shortcut is gated by saying what it is, which is the only thing its author knows.
 */

import { PANEL_IDS } from '../core/PanelManager.js';
import { toggleMidiLearnMode } from './midi/midiLearnToggle.js';
import { voiceRegistry } from '../voice/VoiceRegistry.js';
import { getCameraFocus, setCameraFocus } from '../world/cameraFocus.js';
import { roomStore } from '../room/roomStore.js';

const PANEL_SHORTCUTS = Object.freeze({
    KeyJ: PANEL_IDS.JAMMING,
    KeyP: PANEL_IDS.PLAYBACK,
    KeyC: PANEL_IDS.COSMIC_LFO,
    KeyS: PANEL_IDS.SENSORS,
});

const DIMENSION_KEY_CODES = ['Digit1', 'Digit2', 'Digit3'];

/**
 * What a shortcut acts ON — and therefore what has to be true before it may act at all.
 *
 * `VOICE` — it drives the focused deck(s). Gated: the targets are filtered down to the decks this
 * player holds, and the key is refused outright when none are left.
 *
 * `CLIENT` — it is about this machine and no deck. Learn mode is a property of the MIDI hardware in
 * front of this person; a room has no opinion on it, and gating it would strand a spectator who
 * simply wants their own controller mapped before their turn.
 */
const SCOPE = Object.freeze({ VOICE: 'voice', CLIENT: 'client' });

function isTextInputTarget(target) {
    if (!target || typeof target !== 'object') {
        return false;
    }
    if (target.isContentEditable) return true;
    if (typeof target.getAttribute === 'function') {
        const role = target.getAttribute('role');
        if (role === 'textbox' || role === 'combobox') {
            return true;
        }
    }
    const tagName = typeof target.tagName === 'string' ? target.tagName.toUpperCase() : '';
    return tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT';
}

function normalizeDimensionId(definition) {
    if (!definition) return null;
    return (
        definition.id ||
        definition.dimensionId ||
        definition.dimension?.id ||
        definition?.label ||
        null
    );
}

/**
 * The decks a voice-scoped shortcut may act on — or `null` when it may act on none and the key has
 * to be refused.
 *
 * Multi-selection returns every selected voice; single-focus returns just the active one. So a
 * shortcut gangs across a shift-selection and acts on the one focused voice otherwise.
 *
 * Outside a room that list is the answer, including when it is EMPTY: nothing focused is the
 * single-orbiter boot the handlers' own fallbacks exist for, and that path is untouched. Inside a
 * room the empty list is not a fallback but a refusal — every act in a room names a deck, and a
 * deck nobody focused is not one this player may be assumed to hold.
 */
function getDrivableFocusTargets() {
    const focused = voiceRegistry.getFocusTargets();
    if (!roomStore.isActive) return focused;
    const drivable = focused.filter((voiceId) => roomStore.canSelfDrive(voiceId));
    return drivable.length > 0 ? drivable : null;
}

/**
 * May a shortcut fall back to the cached `orbiter` / `worldModeController` / `panelManager` deps?
 * Never in a room.
 *
 * Those predate the registry and are last-writer-wins across tiles — every tile's boot calls
 * `updateDependencies`, so they point at whichever booted last rather than the one under the user's
 * hand. Alone that is merely imprecise, and the legacy single-orbiter boot has nothing else to use.
 * In a room it is a way straight back around the gate: registration is two-phase and some voices
 * register with no manager at all, so a key aimed at ONE deck whose own manager has not landed yet
 * would fall through and drive a DIFFERENT tile's — one the room never got to have an opinion on.
 * Doing nothing is the honest answer; the deck's own manager registers a moment later.
 */
function mayUseCachedDeps() {
    return !roomStore.isActive;
}

export class KeyboardController {
    static #instance = null;

    /**
     * Initializes (or updates) the singleton instance.
     * @param {object} deps
     * @param {object} deps.orbiter - Current orbiter instance for transport actions.
     * @param {object} deps.worldModeController - Controller that exposes dimension helpers.
     * @param {object} deps.panelManager - Panel manager singleton for panel switching.
     * @returns {KeyboardController}
     */
    static initialize(deps = {}) {
        if (KeyboardController.#instance) {
            KeyboardController.#instance.updateDependencies(deps);
            return KeyboardController.#instance;
        }
        KeyboardController.#instance = new KeyboardController(deps);
        return KeyboardController.#instance;
    }

    constructor({ orbiter = null, worldModeController = null, panelManager = null } = {}) {
        if (KeyboardController.#instance) {
            throw new Error('KeyboardController is a singleton. Use KeyboardController.initialize().');
        }
        this.orbiter = orbiter;
        this.worldModeController = worldModeController;
        this.panelManager = panelManager;

        this.keyHandlers = new Map();
        this.activeKeys = new Set();
        // Guards Shift+Space against overlapping itself — see `playFromBeginning`.
        this._restartInFlight = false;
        this.isFocused = typeof document === 'undefined' ? false : document.hasFocus();
        this.lastFocusEventTs = 0;

        this._boundKeyDown = this.handleKeyDown.bind(this);
        this._boundKeyUp = this.handleKeyUp.bind(this);
        this._boundMarkFocused = this.markFocused.bind(this);
        this._boundHandleBlur = this.handleBlur.bind(this);
        this._boundHandleFocus = this.handleFocus.bind(this);
        this._boundHandleVisibility = this.handleVisibilityChange.bind(this);

        this.attachListeners();
        this.registerDefaultLayers();
    }

    updateDependencies({ orbiter = null, worldModeController = null, panelManager = null } = {}) {
        if (orbiter) this.orbiter = orbiter;
        if (worldModeController) this.worldModeController = worldModeController;
        if (panelManager) this.panelManager = panelManager;
    }

    attachListeners() {
        if (typeof document === 'undefined' || typeof window === 'undefined') {
            return;
        }
        document.addEventListener('keydown', this._boundKeyDown, true);
        document.addEventListener('keyup', this._boundKeyUp, true);
        document.addEventListener('pointerdown', this._boundMarkFocused, true);
        document.addEventListener('pointerup', this._boundMarkFocused, true);
        document.addEventListener('mousedown', this._boundMarkFocused, true);
        document.addEventListener('touchstart', this._boundMarkFocused, { passive: true, capture: true });
        window.addEventListener('blur', this._boundHandleBlur, true);
        window.addEventListener('focus', this._boundHandleFocus, true);
        document.addEventListener('visibilitychange', this._boundHandleVisibility, true);
    }

    detachListeners() {
        if (typeof document === 'undefined' || typeof window === 'undefined') {
            return;
        }
        document.removeEventListener('keydown', this._boundKeyDown, true);
        document.removeEventListener('keyup', this._boundKeyUp, true);
        document.removeEventListener('pointerdown', this._boundMarkFocused, true);
        document.removeEventListener('pointerup', this._boundMarkFocused, true);
        document.removeEventListener('mousedown', this._boundMarkFocused, true);
        document.removeEventListener('touchstart', this._boundMarkFocused, true);
        window.removeEventListener('blur', this._boundHandleBlur, true);
        window.removeEventListener('focus', this._boundHandleFocus, true);
        document.removeEventListener('visibilitychange', this._boundHandleVisibility, true);
    }

    registerDefaultLayers() {
        // Transport (Space toggles, Shift+Space restarts from zero)
        this.registerKeyHandler('Space', (event, targetIds) => {
            if (event?.shiftKey) {
                // Always a restart, never a pause. Shift+Space used to fall through to
                // `togglePlayback` whenever the ACTIVE voice happened to be playing, which made the
                // key mean two different things depending on a state the user cannot see across a
                // selection — and meant a selection with one deck already running could never be
                // restarted at all. Plain Space is the toggle; this one only ever starts from zero.
                void this.playFromBeginning(targetIds);
                return;
            }
            // No `this.orbiter` guard: that cached dependency is last-writer-wins across tiles and
            // is null in the collection shell until some tile boots, while `togglePlayback` already
            // resolves per-voice transports from the registry and guards its own fallback.
            this.togglePlayback(targetIds);
        }, { scope: SCOPE.VOICE });

        // Dimensions (1,2,3)
        DIMENSION_KEY_CODES.forEach((code, index) => {
            this.registerKeyHandler(
                code,
                (_event, targetIds) => this.setActiveDimensionByIndex(index, targetIds),
                { requirePlainModifiers: true, scope: SCOPE.VOICE },
            );
        });

        // Panels (J, P, C, S). Voice-scoped even though a panel makes no sound: which panel a deck
        // is showing is mirrored to the whole room, so a non-holder switching their own copy is not
        // a private view — it is this client quietly disagreeing with everyone else about a deck,
        // until the next remote change snaps it back.
        Object.entries(PANEL_SHORTCUTS).forEach(([code, panelId]) => {
            this.registerKeyHandler(
                code,
                (_event, targetIds) => this.activatePanel(panelId, targetIds),
                { requirePlainModifiers: true, throttleMs: 150, scope: SCOPE.VOICE },
            );
        });

        this.registerKeyHandler(
            'KeyM',
            () => this.toggleMidiLearnMode(),
            { requirePlainModifiers: true, throttleMs: 300, scope: SCOPE.CLIENT },
        );

        // Q — what the camera orbits: the world, or its moon. The same choice the button in the
        // bottom-left corner makes; both write the one store the scene reads every frame — and that
        // store is mirrored to the room, so this is the deck's camera, not the viewer's.
        this.registerKeyHandler(
            'KeyQ',
            (_event, targetIds) => this.toggleCameraFocus(targetIds),
            { requirePlainModifiers: true, throttleMs: 150, scope: SCOPE.VOICE },
        );
    }

    /**
     * Send the focused voices' cameras to the other body.
     *
     * Every other shortcut here SETS a thing (this panel, this dimension), so ganging one across a
     * shift-selection needs no thought — the same value lands on each. This one TOGGLES, and a
     * toggle applied voice by voice would flip each about its own state: a selection that started
     * disagreed would stay disagreed forever, and Q would never be able to settle it. So the
     * selection is read ONCE, from the voice the key was aimed at, and the answer is written to all
     * of them. One key, one answer — press it again and they cross back together.
     *
     * The voice it is aimed at is the ACTIVE one, which is not the same as the first of the
     * selection: shift-clicking a second orbiter makes THAT one active while the selection keeps
     * its insertion order, so the first id is the voice you selected first, not the one you last
     * touched. Reading the first would toggle about a camera the user is not looking at, and the
     * orbiter under their hand would appear to ignore the key.
     */
    toggleCameraFocus(targetIds = getDrivableFocusTargets()) {
        if (targetIds === null) return;
        const activeId = voiceRegistry.getActive()?.id ?? null;
        const ids = targetIds.length ? targetIds : [activeId].filter(Boolean);
        if (!ids.length) return;

        const aimedAt = activeId && ids.includes(activeId) ? activeId : ids[0];
        const next = getCameraFocus(aimedAt) === 'moon' ? 'world' : 'moon';
        ids.forEach((id) => setCameraFocus(id, next));
    }

    registerKeyHandler(code, handler, options = {}) {
        if (!code || typeof handler !== 'function') {
            return;
        }
        this.keyHandlers.set(code, {
            handler,
            options: {
                requirePlainModifiers: Boolean(options.requirePlainModifiers),
                throttleMs: typeof options.throttleMs === 'number' ? Math.max(options.throttleMs, 0) : 0,
                // Unstated scope defaults to VOICE, the answer that is safe to be wrong about: a
                // shortcut wrongly gated is inert on a deck you don't hold, where a shortcut wrongly
                // ungated is the bug this whole seam exists to close.
                scope: options.scope === SCOPE.CLIENT ? SCOPE.CLIENT : SCOPE.VOICE,
                lastInvokedAt: 0,
            },
        });
    }

    shouldHandleEvent(event) {
        if (!event || event.defaultPrevented) return false;
        if (!this.isFocused) return false;
        if (isTextInputTarget(event.target)) return false;
        return true;
    }

    handleKeyDown(event) {
        const { code } = event;
        if (!code || !this.shouldHandleEvent(event)) return;
        const entry = this.keyHandlers.get(code);
        if (!entry) return;
        if (event.repeat && this.activeKeys.has(code)) {
            return;
        }
        if (entry.options.requirePlainModifiers && (event.altKey || event.ctrlKey || event.metaKey)) {
            return;
        }
        const now = performance.now();
        if (entry.options.throttleMs && now - entry.options.lastInvokedAt < entry.options.throttleMs) {
            return;
        }

        this.activeKeys.add(code);

        // The room's answer for this key, resolved ONCE here rather than by each handler.
        //
        // A refused key is SWALLOWED, not ignored: it is still this app's key, and handing it back
        // to the browser would let Space scroll the studio out from under a player who is merely
        // watching a deck they don't hold. It is also silent — the pointer cover says no by simply
        // not responding, and a room of four people leaning on keys is the last place to start
        // stacking up notifications.
        let targetIds = null;
        if (entry.options.scope === SCOPE.VOICE) {
            targetIds = getDrivableFocusTargets();
            if (targetIds === null) {
                event.preventDefault();
                event.stopPropagation();
                return;
            }
        }

        const result = entry.handler(event, targetIds);
        entry.options.lastInvokedAt = now;
        if (result !== false) {
            event.preventDefault();
            event.stopPropagation();
        }
    }

    handleKeyUp(event) {
        const { code } = event;
        if (!code) return;
        this.activeKeys.delete(code);
    }

    markFocused() {
        this.isFocused = true;
        this.lastFocusEventTs = performance.now();
    }

    handleBlur() {
        this.isFocused = false;
    }

    handleFocus() {
        this.isFocused = true;
    }

    handleVisibilityChange() {
        if (typeof document === 'undefined') return;
        this.isFocused = document.visibilityState === 'visible' && document.hasFocus();
    }

    togglePlayback(targetIds = getDrivableFocusTargets()) {
        if (targetIds === null) return;
        let handled = false;
        for (const id of targetIds) {
            const transport = voiceRegistry.get(id)?.transportControl ?? null;
            if (transport?.toggle) {
                handled = true; // a per-voice transport owns this key — never fall through to the orbiter
                // Room mode: match the transport button. A start routes (quantized) through the room,
                // but a pause is unrouted and would stop only this client — a silent divergence. So
                // while this voice is playing in a room, Space performs the routed, beat-quantized
                // stop (the same synced stop the Stop control publishes) instead of a local pause.
                if (roomStore.isActive && transport.getState?.() === 'playing') {
                    transport.stop?.();
                    continue;
                }
                transport.toggle();
            }
        }
        if (handled) {
            return;
        }

        // Fallback to direct orbiter control (no per-voice transport available) — legacy boots only.
        const orbiter = mayUseCachedDeps() ? this.orbiter : null;
        if (!orbiter) return;
        const isPlaying = this.isPlaybackRunning();
        if (isPlaying) {
            // In a room a pause is unrouted (local-only). Route the beat-quantized stop instead so
            // both sides stay synced — the same rule as the transport path above.
            if (roomStore.isActive) {
                if (typeof orbiter.stop === 'function') orbiter.stop();
                return;
            }
            if (typeof orbiter.pause === 'function') {
                orbiter.pause();
            } else if (typeof orbiter.stop === 'function') {
                orbiter.stop();
            }
            return;
        }
        if (typeof orbiter.play === 'function') {
            orbiter.play();
        }
    }

    /**
     * Shift+Space — every selected voice restarts from the top, together.
     *
     * This shortcut predates multi-focus and was the last one still reading the single-orbiter
     * world: the cached `this.orbiter` (last-writer-wins across tiles — every tile's boot calls
     * `updateDependencies`, so it points at whichever booted last, not the focused one) plus the
     * ACTIVE voice's engine. With two orbiters selected that restarted exactly one of them, and not
     * reliably the one under the user's hand. It now resolves its targets the way every other
     * shortcut here does — live, from the registry, across the whole selection.
     *
     * Being synchronized is the three phases below, and it is why this cannot just loop
     * "seek, then play" voice by voice: a seek is async (a rewind asked of a PLAYING deck is
     * quantized onto that deck's own bar grid; a room seek waits for a shared bar), so a per-voice
     * loop starts the second orbiter only once the first one's rewind has landed — audibly
     * staggered. Instead the whole selection is brought to the same state, rewound together, and
     * only then started in one pass with nothing awaited in between.
     */
    async playFromBeginning(targetIds = getDrivableFocusTargets()) {
        if (targetIds === null) return;
        const voices = this._resolveTransportVoices(targetIds);
        if (!voices.length) return;

        // Re-entrancy: a second press while a restart is mid-flight is dropped, not queued. Both
        // presses mean the same thing — "be at zero, playing" — so the one already running is the
        // answer. Left unguarded the second press reads the state the first one has already begun
        // writing (the transport flips to 'playing' synchronously, long before the engine's unlock
        // and load finish) and rewinds a deck it believes is running, landing the playhead at zero a
        // bar late.
        if (this._restartInFlight) return;
        this._restartInFlight = true;
        try {
            // Phase 1 — the voices still running are PAUSED, which is what makes their rewind land
            // now instead of on their own next bar; that bar is what would otherwise split a
            // selection mixing a playing orbiter with an idle one. Paused, not stopped, on purpose:
            // `TransportControl.stop` also tears down the realm-wide screen capture (see the warning
            // in `export/captureWindow.js`), so a stop here would end the recording of the very
            // performance the user is restarting. Driven through the ENGINE rather than the
            // transport because `TransportControl` dedupes against its own in-flight action and
            // would hand back an unrelated promise having paused nothing.
            //
            // Skipped entirely in a room, where this phase is not merely unnecessary but harmful: a
            // pause is unrouted (local-only), and a stop is worse still — `AudioEngineAdapter.stop`
            // publishes the stop to every peer but only ARMS its own, so the await returns with this
            // deck still playing and the start behind it cancels the armed stop and returns early
            // without ever publishing a start. The peers stop; this client plays on.
            if (!roomStore.isActive) {
                await this._settleAll(
                    voices
                        .filter(({ transport, engine }) => (
                            transport?.getState?.() === 'playing' || engine?.isPlaying?.() === true
                        ))
                        .map(({ engine }) => engine?.pause?.()),
                    'pause',
                );
            }

            // Phase 2 — rewind to the beginning of the audio, and wait for all of it. Explicit
            // rather than leaning on the rewind a stop performs: a start position the user placed
            // earlier is where a plain play would resume, and Shift+Space means zero.
            //
            // In a room this await does NOT mean "rewound" — a room seek anchors itself to a shared
            // beat and returns at once rather than blocking for a bar. That is fine, because there
            // the anchor is the synchronization: the rewind is PUBLISHED, so every client applies it
            // on the same beat, which is a stronger guarantee than anything arranged locally here.
            await this._settleAll(voices.map(({ engine }) => this._seekTransportToStart(engine)), 'rewind');

            // Phase 3 — start them in ONE synchronous pass. Outside a room no `await` may separate
            // these calls; that is the whole of the local synchronization.
            for (const { engine } of voices) {
                // Through the ENGINE, for the same reason phase 1 is: `TransportControl.play` opens
                // with `if (this._actionInFlight) return this._actionInFlight`, so a restart landing
                // while that tile's own Play is still awaiting its unlock/load would be handed the
                // older promise and start nothing — leaving that one voice paused at zero while its
                // siblings run, silently, since the promise it returns resolves fine. The play/pause
                // button still follows: `TransportControl.init` mirrors the engine's playback-state
                // events, which is what already carries the pause in phase 1.
                void Promise.resolve(engine?.play?.()).catch((error) => {
                    console.warn('[KeyboardController] Failed to restart a voice', error);
                });
            }
        } finally {
            this._restartInFlight = false;
        }
    }

    /**
     * The voices a transport shortcut acts on: every SELECTED voice, resolved live from the registry
     * rather than from the cached `this.orbiter`. Falls back to that cached orbiter only when the
     * registry knows nothing — a legacy single-orbiter boot whose engine never registered.
     * @returns {Array<{ engine: object|null, transport: object|null }>}
     */
    _resolveTransportVoices(targetIds = getDrivableFocusTargets()) {
        if (targetIds === null) return [];
        // The fallback hangs off "nothing is focused", NOT "the focused voices turned up no engine".
        // Keyed off the latter, a selection whose engines have not finished booting would fall
        // through to the cached orbiter and restart whichever tile booted last — a voice the user
        // never selected. With nothing focused there is no selection to contradict, so the cached
        // orbiter is the only thing it can mean.
        if (!targetIds.length) {
            return mayUseCachedDeps() && this.orbiter ? [{ engine: this.orbiter, transport: null }] : [];
        }
        return targetIds
            .map((id) => voiceRegistry.get(id))
            .filter(Boolean)
            .map((voice) => ({
                engine: voice.audioEngine ?? null,
                transport: voice.transportControl ?? null,
            }))
            // An ENGINE is the requirement, not merely a transport: the engine is what owns the
            // rewind. Registration is two-phase (identity first, the engine once its async init
            // lands), so a voice can hold a transport and no engine for a moment — starting that one
            // would resume it from wherever it was left, silently, while its siblings begin at zero.
            // Skipping it is the honest answer; the next press catches it.
            .filter(({ engine }) => Boolean(engine));
    }

    /**
     * Wait for one restart phase to land across every voice. Settled, not `Promise.all`'s
     * fail-fast: one deck failing to rewind must not abandon the others mid-restart, which would
     * leave the selection split between the old position and zero.
     * @param {Array<Promise|any>} results one entry per voice (non-promises pass straight through).
     * @param {string} label what the phase was, for the warning.
     */
    _settleAll(results, label) {
        return Promise.all(results.map((result) => Promise.resolve(result).catch((error) => {
            console.warn(`[KeyboardController] Failed to ${label} before restart`, error);
        })));
    }

    _seekTransportToStart(target) {
        if (!target) {
            return null;
        }
        try {
            if (typeof target.seekToMilliseconds === 'function') {
                return target.seekToMilliseconds(0);
            }
            if (typeof target.seek === 'function') {
                return target.seek(0);
            }
        } catch (error) {
            console.warn('[KeyboardController] Failed to seek to start', error);
        }
        return null;
    }

    isPlaybackRunning() {
        try {
            const engine = voiceRegistry.getActive()?.audioEngine ?? null;
            if (engine?.isPlaying) {
                return Boolean(engine.isPlaying());
            }
        } catch (_) {}

        return false;
    }

    setActiveDimensionByIndex(index, targetIds = getDrivableFocusTargets()) {
        if (typeof index !== 'number' || targetIds === null) return;
        if (targetIds.length) {
            for (const id of targetIds) {
                const voice = voiceRegistry.get(id);
                const fallbackMode = targetIds.length === 1 && mayUseCachedDeps() ? this.worldModeController : null;
                this._applyDimensionToVoice(voice?.worldMode ?? fallbackMode, voice?.id ?? null, index);
            }
        } else if (mayUseCachedDeps()) {
            const active = voiceRegistry.getActive();
            this._applyDimensionToVoice(active?.worldMode ?? this.worldModeController, active?.id ?? null, index);
        }
    }

    // Switch ONE voice's mode controller to its index-th dimension and broadcast a voice-stamped
    // dimension-changed event (so only that tile's React surfaces re-read). Extracted so the shortcut can
    // drive either the single focused voice or every selected voice through one code path.
    _applyDimensionToVoice(mode, voiceId, index) {
        // Resolved live from the registry — NOT the cached `this.worldModeController`, which is
        // last-writer-wins across tiles (every tile's boot calls updateDependencies, so it pointed at
        // whichever tile booted last, not the focused one).
        if (!mode) {
            return;
        }
        const list = mode.getAvailableDimensions?.();
        if (!Array.isArray(list) || !list.length) {
            return;
        }
        const target = list[index];
        const dimensionId = normalizeDimensionId(target);
        if (!dimensionId) {
            return;
        }

        const didChange = mode.setActiveDimension?.(dimensionId, {
            source: 'keyboard',
            broadcast: true,
        });
        if (didChange && typeof document !== 'undefined') {
            document.dispatchEvent(
                new CustomEvent('orbiters:dimension-changed', {
                    detail: {
                        dimensionId,
                        label: target?.label ?? target?.dimensionLabel ?? dimensionId,
                        source: 'keyboard',
                        // Stamp the voice this switch acts on — only that tile's React
                        // surfaces re-read (single-orbiter null → byte-identical).
                        voiceId,
                    },
                }),
            );
        }
    }

    activatePanel(panelId, targetIds = getDrivableFocusTargets()) {
        if (!panelId || targetIds === null) {
            return;
        }
        if (targetIds.length === 0) {
            const panelManager = mayUseCachedDeps() ? this.panelManager : null;
            if (panelManager?.activatePanel && panelManager.getActivePanel?.() !== panelId) {
                panelManager.activatePanel(panelId);
            }
            return;
        }
        for (const id of targetIds) {
            const fallbackManager = targetIds.length === 1 && mayUseCachedDeps() ? this.panelManager : null;
            const panelManager = voiceRegistry.get(id)?.panelManager ?? fallbackManager;
            if (!panelManager?.activatePanel) continue;
            if (panelManager.getActivePanel?.() === panelId) continue;
            panelManager.activatePanel(panelId);
        }
    }

    toggleMidiLearnMode() {
        // Direct import — learn mode is voice-independent, so this works with ZERO voices
        // loaded (the collection studio shell initializes this singleton at boot exactly for
        // that). The old window-global indirection + panel fallback are gone with it.
        void toggleMidiLearnMode();
    }
}

export default KeyboardController;
